import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/Label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { 
  Shield, 
  Users, 
  Eye, 
  PenSquare, 
  CheckSquare, 
  Download, 
  AlertCircle, 
  Plus, 
  Trash2,
  Info,
  Check,
  Filter,
  Settings,
  Save,
  Upload,
  FileText,
  CreditCard,
  Calendar,
  UserPlus,
  RefreshCw,
  BarChart3,
  Crown,
  UserCheck,
  DollarSign,
  BookOpen,
  UtensilsCrossed,
  School,
  GraduationCap,
  Network,
  Wallet,
  ClipboardCheck,
  MessageSquare,
  Briefcase,
  Clock,
  Lock,
  Boxes,
  KeyRound,
  ChevronRight,
  RotateCcw,
  ShieldOff,
  ShieldCheck,
  Pencil,
  Layers,
  Link2,
  Link2Off,
  Package
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Toast } from './ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useFeatures } from '../contexts/FeaturesContext';
import { clesLicence } from '../config/licenceModules';

// ---------------------------------------------------------------------------
// Métadonnées d'affichage des modules
// ---------------------------------------------------------------------------
// L'arborescence des permissions vient du serveur, pas d'ici : ces entrées ne
// servent qu'à donner un libellé lisible, une icône et une couleur. Un module
// absent de cette table s'affiche quand même, avec son nom technique — c'est
// voulu, pour qu'une permission nouvellement créée soit immédiatement
// configurable sans attendre une livraison du frontend.
const META_MODULES = {
  ELEVES:                  { name: 'Élèves', icon: Users, color: 'bg-blue-500', description: 'Dossiers, admissions, radiations', ordre: 10 },
  CANTINE:                 { name: 'Cantine scolaire', icon: UtensilsCrossed, color: 'bg-orange-500', description: 'Inscriptions, menus, pointage', ordre: 20 },
  CLASSE_ET_MATIERES:      { name: 'Classes & matières — accès', icon: School, color: 'bg-indigo-500', description: 'Porte du module', ordre: 30 },
  CLASSES:                 { name: 'Classes & matières', icon: School, color: 'bg-indigo-500', description: 'Classes, matières, affectations', ordre: 31 },
  CARNET_EVALUATION:       { name: "Carnet d'évaluations — accès", icon: BookOpen, color: 'bg-emerald-600', description: 'Porte du module', ordre: 40 },
  NOTES:                   { name: "Carnet d'évaluations", icon: BookOpen, color: 'bg-emerald-600', description: 'Notes, bulletins, appréciations', ordre: 41 },
  SUIVI_ENSEIGNANTS:       { name: 'Suivi des enseignants', icon: ClipboardCheck, color: 'bg-teal-600', description: 'Programmes, cours et appels', ordre: 50 },
  RESSOURCE_HUMAINES:      { name: 'Ressources humaines', icon: UserCheck, color: 'bg-violet-500', description: 'Employés, contrats, documents', ordre: 60 },
  POINTAGES:               { name: 'Pointage du personnel', icon: Clock, color: 'bg-violet-400', description: 'Pointages et kiosque', ordre: 61 },
  COMPTABILITE_ET_PAIE:    { name: 'Comptabilité & paie', icon: Wallet, color: 'bg-amber-600', description: 'Encaissements, caisses, salaires', ordre: 70 },
  INSCRIPTION_AUX_EXAMENS: { name: 'Examens', icon: GraduationCap, color: 'bg-rose-500', description: 'Inscriptions, candidats libres', ordre: 80 },
  MESSAGERIE:              { name: 'Messagerie', icon: MessageSquare, color: 'bg-sky-500', description: 'Conversations internes', ordre: 90 },
  TACHES:                  { name: 'Tâches inter-services', icon: CheckSquare, color: 'bg-cyan-600', description: 'Attribution et suivi', ordre: 91 },
  LIAISON_INTER_SITES:     { name: 'Liaison inter-sites', icon: Network, color: 'bg-fuchsia-600', description: 'Porte du module', ordre: 95 },
  MISSION_CONTROL:         { name: "Clôture d'année", icon: RefreshCw, color: 'bg-lime-600', description: 'Transitions et clôture', ordre: 100 },
  TENDANCES_ET_ANALYSE:    { name: 'Tendances & analyse', icon: BarChart3, color: 'bg-slate-600', description: 'Affluence, statistiques', ordre: 110 },
  LOGISTIQUE:              { name: 'Logistique', icon: Package, color: 'bg-teal-700', description: 'Parc des actifs, étiquettes, remises', ordre: 115 },
  OUTILS_ADMINISTRATIFS:   { name: 'Outils administratifs — accès', icon: Briefcase, color: 'bg-gray-600', description: 'Porte du module', ordre: 120 },
  ADMINISTRATION:          { name: 'Outils administratifs', icon: Briefcase, color: 'bg-gray-600', description: 'Droits, support, années scolaires', ordre: 121 },
  PARAMETRES:              { name: 'Paramètres', icon: Settings, color: 'bg-gray-500', description: "Réglages de l'établissement", ordre: 122 },
  ALL_MODULES:             { name: 'Tous les modules', icon: Crown, color: 'bg-red-600', description: 'Accès total — à réserver', ordre: 900 }
};

// Libellés des sous-modules. Même principe : purement cosmétique, un
// sous-module inconnu s'affiche avec son nom technique.
const META_SOUS_MODULES = {
  LISTE: 'Liste', DETAILS: 'Détails', CREATION: 'Création',
  EMPLOYES: 'Employés', CONTRATS: 'Contrats', DOCUMENTS: 'Documents',
  ACCES: "Accès à l'application", RADIATION: 'Radiation', FINANCE: 'Finances',
  CARTE: 'Carte étudiante', EMPLOIS_DU_TEMPS: 'Emplois du temps', ADMISSIONS: 'Admissions',
  TABLEAU_DE_BORD: 'Tableau de bord', ENCAISSEMENTS: 'Encaissements',
  PORTEFEUILLE: 'Portefeuille', CAISSE_CENTRALE: 'Caisse centrale', DEPENSES: 'Dépenses',
  RECUS: 'Reçus', SALAIRES: 'Salaires', AVANCES: 'Avances sur salaire',
  INSCRIPTIONS: 'Inscriptions', POINTAGE: 'Pointage', MENUS: 'Menus',
  CANDIDATS_LIBRES: 'Candidats libres', PAIEMENTS: 'Paiements',
  STATISTIQUES: 'Statistiques', CONFIGURATION: 'Configuration',
  AFFLUENCE: 'Affluence', DECISIONNEL: 'Outil décisionnel',
  MATIERES: 'Matières', AFFECTATIONS: 'Affectations', SECTIONS: 'Sections', SEUILS: 'Seuils',
  ASSOCIATIONS: 'Associations matières-classes',
  SAISIE: 'Saisie des notes', BULLETINS: 'Bulletins', APPRECIATIONS: 'Appréciations',
  VERROUILLAGE: 'Verrouillage', SUPPORT: 'Centre de support',
  ANNEE_SCOLAIRE: 'Années scolaires', GENERAUX: 'Réglages généraux',
  PERMISSIONS: 'Droits et permissions', KIOSK: 'Kiosque', GENERAL: 'Actions générales',
  PARC: 'Parc des actifs', TRANSPORT: 'Bus scolaire'
};

/**
 * Decoupe une cle de permission en module, sous-module et action.
 *
 * Filet de securite uniquement : le serveur envoie deja `module`, `sous_module`
 * et `action` avec chaque permission, et ce sont eux qui font foi. Douze cles
 * de csap1 ne s'accordent d'ailleurs pas avec leurs colonnes — MISSION_CONTROL
 * .ADMIN porte en base le sous-module ADMINISTRATION et l'action VIEW — donc
 * deduire de la cle donnerait un resultat faux la ou les colonnes sont justes.
 */
const decouperCle = (cle) => {
  const parts = cle.split('.');
  if (parts.length === 1) return { module: parts[0], sousModule: null, action: null };
  if (parts.length === 2) return { module: parts[0], sousModule: null, action: parts[1] };
  return {
    module: parts[0],
    sousModule: parts.slice(1, -1).join('.'),
    action: parts[parts.length - 1]
  };
};

const ICONES_SOUS_MODULE = {
  LISTE: Users, DETAILS: Eye, DOCUMENTS: FileText, FINANCE: CreditCard,
  EMPLOIS_DU_TEMPS: Calendar, RADIATION: AlertCircle, CARTE: Shield,
  ADMISSIONS: UserPlus, TABLEAU_DE_BORD: BarChart3, SALAIRES: DollarSign,
  ENCAISSEMENTS: DollarSign, RECUS: FileText, STATISTIQUES: BarChart3,
  CONFIGURATION: Settings, MENUS: BookOpen, POINTAGE: Clock
};

/**
 * Replie le catalogue plat du serveur en modules → sous-modules → actions.
 *
 * Ne s'appuie que sur les colonnes `module`, `sous_module` et `action` :
 * aucune connaissance des modules n'est codée ici. Une permission créée par
 * migration — ou poussée un jour depuis le CIK — apparaît donc dans l'écran
 * sans livraison de frontend.
 */
const construireModules = (permissions) => {
  const parModule = new Map();

  for (const p of permissions) {
    if (!p.module) continue;

    if (!parModule.has(p.module)) {
      parModule.set(p.module, { sousModules: new Map(), aUnAcces: false });
    }
    const mod = parModule.get(p.module);

    // La porte du module est ajoutée à part par getModulePermissions
    if (p.action === 'ACCESS' && !p.sous_module) {
      mod.aUnAcces = true;
      continue;
    }

    // Une action portée directement par le module (ex. CLASSES.CREATE) n'a pas
    // de sous-module : on la range sous une entrée générale plutôt que de la
    // perdre.
    const sm = p.sous_module || 'GENERAL';
    // Un `Map` et non un `Set` : la description du serveur — en francais, et
    // renseignee sur les 215 permissions — n'avait nulle part ou survivre dans
    // un ensemble de chaines, et l'ecran affichait la cle technique a sa place.
    if (!mod.sousModules.has(sm)) mod.sousModules.set(sm, new Map());
    mod.sousModules.get(sm).set(p.action, p.description || null);
  }

  const modules = [];
  for (const [cle, contenu] of parModule) {
    const meta = META_MODULES[cle];

    // Un module inconnu de META_MODULES s'affiche quand même, avec son nom
    // technique : mieux vaut une entrée brute qu'une permission invisible.
    modules.push({
      key: cle,
      name: meta ? meta.name : cle.replace(/_/g, ' '),
      icon: meta ? meta.icon : Shield,
      color: meta ? meta.color : 'bg-gray-400',
      description: meta ? meta.description : 'Module non décrit',
      ordre: meta ? meta.ordre : 500,
      aUnAcces: contenu.aUnAcces,
      subModules: [...contenu.sousModules.entries()]
        .map(([nom, actions]) => ({
          name: nom,
          displayName: META_SOUS_MODULES[nom] || nom.replace(/_/g, ' '),
          icon: ICONES_SOUS_MODULE[nom] || Settings,
          permissions: [...actions.keys()].sort(),
          descriptions: actions
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'fr'))
    });
  }

  return modules.sort((a, b) => a.ordre - b.ordre || a.name.localeCompare(b.name, 'fr'));
};

// PARAMETRAGE-MODULES.md §5, regime B. `border border-transparent` sur le
// bouton plein : sans elle il fait 32 px la ou son voisin bordé en fait 34.
const BOUTON_SECTION =
  'flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg transition-all';

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons. A ne pas confondre avec `BOUTON_SECTION`,
// qui est celui d'un en-tete de section.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const RolesPermissionsManager = ({ addSecurityLog }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [securityGroups, setSecurityGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [applicationModules, setApplicationModules] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  // `erreurChargement` prend la page entiere - c'est son role : sans donnees,
  // il n'y a rien a afficher. Un echec d'ENREGISTREMENT, lui, passe par un
  // toast : l'ecran doit rester, et les bascules en attente avec lui.
  const [erreurChargement, setErreurChargement] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Regime B (PARAMETRAGE-MODULES.md §5) : on modifie en local, puis on valide.
  // `reference` garde le dernier etat connu du serveur, par groupe, pour savoir
  // ce qui a bouge et pour pouvoir revenir en arriere.
  const [reference, setReference] = useState({});
  const [filterPermissions, setFilterPermissions] = useState('all');
  
  // NOUVEAU : État pour l'animation d'expansion
  const [isModuleExpanded, setIsModuleExpanded] = useState(false);
  
  const { refreshPermissions } = usePermissions();
  const { auMoinsUne } = useFeatures();

  const avertir = (message, type = 'success') => setToast({ show: true, message, type });

  // Un instantane {cle: booleen} par groupe : c'est a lui qu'on compare pour
  // savoir s'il reste quelque chose a enregistrer.
  const instantane = (groupes) => {
    const carte = {};
    (groupes || []).forEach((g) => {
      carte[g.id] = Object.fromEntries(
        Object.entries(g.permissions || {}).map(([cle, v]) => [cle, Boolean(v?.enabled)])
      );
    });
    return carte;
  };

  // -------------------------------------------------------------------------
  // Composition licence x droits
  // -------------------------------------------------------------------------
  // Regler les droits d'un module que l'ecole n'a pas souscrit ne produit rien :
  // la garde serveur `requireFeature` refusera la requete quels que soient les
  // droits accordes. On ne le propose donc pas.
  //
  // Decision du 16 septembre 2026 : un module non souscrit n'est plus affiche
  // du tout — ni carte grisee, ni mention. Une ecole qui a cinq modules n'a pas
  // a parcourir les autres. Les droits deja enregistres sont conserves
  // intacts : si la licence evolue, tout reapparait tel quel.
  const moduleSousLicence = useCallback((cleModule) => {
    const cles = clesLicence(cleModule);
    return cles.length === 0 || auMoinsUne(cles);
  }, [auMoinsUne]);

  const modulesAffiches = useMemo(() => {
    return applicationModules
      .map((m) => ({ ...m, sousLicence: moduleSousLicence(m.key) }))
      .filter((m) => m.sousLicence);
  }, [applicationModules, moduleSousLicence]);

  // ------------------------------------------------------------------------
  // Ce que le groupe porte, d'un seul regard
  // ------------------------------------------------------------------------
  // L'ecran se parcourt module par module : pour savoir ce qu'un groupe peut
  // faire, il fallait ouvrir chaque module l'un apres l'autre. La synthese
  // replie les memes droits par module, sans changer de page.
  //
  // Elle part de `selectedGroup.permissions`, la meme source que le compteur
  // « N permissions actives » de la carte : les deux chiffres ne peuvent donc
  // pas diverger. Elle ne passe pas par l'arborescence des modules, ce qui lui
  // permet de montrer aussi les droits dont le module n'est plus au catalogue —
  // invisibles autrement, puisqu'aucune carte ne les porte.
  const syntheseGroupe = useMemo(() => {
    if (!selectedGroup?.permissions) return null;

    const parModule = new Map();
    let total = 0;

    for (const [cle, valeur] of Object.entries(selectedGroup.permissions)) {
      if (!valeur?.enabled) continue;

      // Les colonnes du serveur d'abord ; la cle ne sert que si elles manquent.
      const repli = decouperCle(cle);
      const module = valeur.module || repli.module;
      // Module non souscrit : ses droits restent en base, mais ne s'affichent
      // pas, comme ses cartes (16 septembre 2026).
      if (!moduleSousLicence(module)) continue;
      total += 1;
      const sousModule = valeur.sous_module || repli.sousModule;
      const action = valeur.action || repli.action;

      if (!parModule.has(module)) parModule.set(module, []);
      parModule.get(module).push({
        cle,
        action,
        // Chacune des 215 permissions porte une description redigee : c'est
        // elle qu'on lit, pas un libelle reconstruit ici.
        libelle: valeur.description
          || (action || cle).replace(/_/g, ' ').toLowerCase(),
        sousModule,
        sousModuleLibelle: sousModule
          ? (META_SOUS_MODULES[sousModule] || sousModule.replace(/_/g, ' '))
          : null
      });
    }

    const lignes = [...parModule.entries()].map(([cleModule, droits]) => {
      const meta = META_MODULES[cleModule];
      // Le module tel que le catalogue le connait : absent, la ligne reste
      // affichee mais ne mene nulle part.
      const auCatalogue = applicationModules.find((m) => m.key === cleModule) || null;
      return {
        key: cleModule,
        name: meta ? meta.name : cleModule.replace(/_/g, ' '),
        icon: meta ? meta.icon : Shield,
        ordre: meta ? meta.ordre : 500,
        sousLicence: moduleSousLicence(cleModule),
        auCatalogue,
        droits: droits.sort((a, b) =>
          (a.sousModuleLibelle || '').localeCompare(b.sousModuleLibelle || '', 'fr')
          || String(a.libelle).localeCompare(String(b.libelle), 'fr')
        )
      };
    });

    lignes.sort((a, b) => a.ordre - b.ordre || a.name.localeCompare(b.name, 'fr'));

    return {
      total,
      lignes,
      horsLicence: lignes.filter((l) => !l.sousLicence).length,
      horsCatalogue: lignes.filter((l) => !l.auCatalogue).length
    };
  }, [selectedGroup, applicationModules, moduleSousLicence]);


  // Structure de base pour le module ÉLÈVES (sera enrichie par le backend)
  const elevesModuleBase = {
    key: 'ELEVES',
    name: 'ÉLÈVES',
    icon: Users,
    description: 'Gestion des élèves, inscriptions, dossiers',
    color: 'bg-blue-500'
  };

  // Structure des sous-modules ELEVES depuis ton code original
  const elevesModuleStructure = {
    name: 'ELEVES',
    subModules: [
      {
        name: 'LISTE',
        displayName: 'Liste des élèves',
        icon: Users,
        permissions: ['VIEW', 'SEARCH', 'FILTER', 'EXPORT_EXCEL', 'EXPORT_PDF']
      },
      {
        name: 'DETAILS',
        displayName: 'Détails des élèves',
        icon: Eye,
        permissions: ['VIEW', 'EDIT', 'PHOTO_MANAGE']
      },
      {
        name: 'RADIATION',
        displayName: 'Radiation des élèves',
        icon: AlertCircle,
        permissions: ['CREATE', 'VIEW_LIST', 'RESTORE', 'DELETE']
      },
      {
        name: 'DOCUMENTS',
        displayName: 'Documents des élèves',
        icon: FileText,
        permissions: ['VIEW', 'UPLOAD', 'DELETE', 'DOWNLOAD']
      },
      {
        name: 'FINANCE',
        displayName: 'Finances des élèves',
        icon: CreditCard,
        permissions: ['VIEW', 'PAYMENT_ADD', 'RECEIPT_GENERATE', 'CLASS_CHANGE']
      },
      {
        name: 'CARTE',
        displayName: 'Carte étudiante',
        icon: Shield,
        permissions: ['GENERATE']
      },
      {
        name: 'EMPLOIS_DU_TEMPS',
        displayName: 'Emplois du temps',
        icon: Calendar,
        permissions: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'FILTER', 'EXPORT_EXCEL', 'EXPORT_PDF']
      },
      {
        name: 'ADMISSIONS.INSCRIPTIONS',
        displayName: 'Inscriptions',
        icon: UserPlus,
        permissions: ['CREATE', 'EDIT', 'UPLOAD_DOCUMENTS', 'MANAGE_FINANCE', 'GENERATE_MATRICULE']
      },
      {
        name: 'ADMISSIONS.REINSCRIPTIONS',
        displayName: 'Réinscriptions',
        icon: RefreshCw,
        permissions: ['CREATE', 'APPROVE', 'REJECT', 'VIEW_HISTORY', 'EXPORT']
      }
    ]
  };


  const notesModuleBase = {
  key: 'NOTES',
  name: 'NOTES',
  icon: FileText,
  description: 'Gestion des notes, verrouillage et déverrouillage',
  color: 'bg-orange-500'
};

const notesModuleStructure = {
  name: 'NOTES',
  subModules: [
    {
      name: 'VERROUILLAGE',
      displayName: 'Verrouillage des Notes',
      icon: Shield,
      permissions: ['VIEW', 'UNLOCK', 'CONFIG', 'HISTORY']
    }
  ]
};




  // Charger les données depuis le backend
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErreurChargement(null);
      
      // Charger les groupes de sécurité existants
      const securityResponse = await api.groupesSecurite.getAllWithPermissions();
      if (securityResponse && securityResponse.success) {
        setSecurityGroups(securityResponse.data);
        setReference(instantane(securityResponse.data));

        // Sélectionner le premier groupe par défaut
        const firstGroup = securityResponse.data?.length > 0 ? securityResponse.data[0] : null;
        if (firstGroup) {
          setSelectedGroup(firstGroup);
        }
      }
      
      // ------------------------------------------------------------------
      // L'arborescence vient du catalogue du serveur.
      //
      // Elle était auparavant construite ici à partir de deux structures
      // écrites en dur — ELEVES et NOTES. Toute permission ajoutée en base
      // restait donc invisible dans cet écran, et les huit modules n'ayant
      // que leur porte étaient impossibles à régler.
      //
      // Les constantes `elevesModuleStructure` et `notesModuleStructure`
      // plus haut ne servent plus : elles restent le temps de vérifier que
      // rien d'autre ne s'y réfère.
      // ------------------------------------------------------------------
      const catalogue = await api.permissions.getAll();
      if (catalogue && catalogue.success && Array.isArray(catalogue.data)) {
        const modules = construireModules(catalogue.data);
        setApplicationModules(modules);
        console.log(`${modules.length} modules construits depuis le catalogue`);
      } else {
        console.warn('Catalogue de permissions indisponible');
        setApplicationModules([]);
      }

      console.log('Données chargées');
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setErreurChargement(error.message);
    } finally {
      setIsLoading(false);
    }
  };




  // Sélectionner un groupe de sécurité
  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setSelectedModule(null); // Reset module selection
    setIsModuleExpanded(false); // Reset animation state
    console.log(`Groupe sélectionné: ${group.nom}`);
  };

  // NOUVEAU : Sélectionner un module avec animation
  const handleSelectModule = (module) => {
    // Filet : la carte d'un module hors licence n'est deja pas cliquable, mais
    // ce garde couvre toute selection par un autre chemin (restauration d'un
    // etat, raccourci ajoute plus tard).
    if (!moduleSousLicence(module.key)) {
      console.warn(`[LICENCE] ${module.key} hors licence : selection ignoree`);
      return;
    }
    setSelectedModule(module);
    // Animation d'expansion après un petit délai
    setTimeout(() => {
      setIsModuleExpanded(true);
    }, 200);
    console.log(`Module sélectionné: ${module.name}`);
  };

  // NOUVEAU : Revenir à la vue modules
  const handleBackToModules = () => {
    setIsModuleExpanded(false);
    // Remettre selectedModule à null après l'animation
    setTimeout(() => {
      setSelectedModule(null);
    }, 300);
  };

  // Obtenir les permissions du module sélectionné
  const getModulePermissions = () => {
    if (!selectedGroup || !selectedModule) return [];
    
    const modulePermissions = [];
    const moduleKey = selectedModule.key;
    
    // Si on a des sous-modules définis, les utiliser
    if (selectedModule.subModules && selectedModule.subModules.length > 0) {
      selectedModule.subModules.forEach(subModule => {
        subModule.permissions.forEach(permission => {
          const permissionKey = `${moduleKey}.${subModule.name}.${permission}`;
          
          // Vérifier si cette permission existe dans le groupe sélectionné
          const groupPermission = selectedGroup.permissions[permissionKey];
          
          if (groupPermission) {
            modulePermissions.push({
              key: permissionKey,
              subModule: subModule.name,
              subModuleDisplayName: subModule.displayName,
              action: permission,
              // Le libelle que lira l'utilisateur. Absent, on retombe sur la
              // cle technique : mieux vaut « EXPORT_PDF » qu'une ligne vide.
              libelle: subModule.descriptions?.get(permission) || null,
              enabled: groupPermission?.enabled || false,
              isSystemDefault: groupPermission?.isSystemDefault || false,
              id: groupPermission?.id
            });
          }
        });
      });
    }
    
    // Ajouter la permission d'accès au module si elle existe
    const accessPermissionKey = `${moduleKey}.ACCESS`;
    const accessPermission = selectedGroup.permissions[accessPermissionKey];
    
    if (accessPermission) {
      modulePermissions.unshift({
        key: accessPermissionKey,
        subModule: 'ACCESS',
        subModuleDisplayName: 'Accès au Module',
        action: 'ACCESS',
        libelle: "Ouvrir le module",
        enabled: accessPermission?.enabled || false,
        isSystemDefault: accessPermission?.isSystemDefault || false,
        id: accessPermission?.id
      });
    }
    
    return modulePermissions;
  };

  // `true` pour « tout activer », `false` pour « tout desactiver », `null`
  // quand aucune confirmation n'est en cours.
  const [basculeEnMasse, setBasculeEnMasse] = useState(null);

  // Gestion des groupes et des rôles. Jusqu'ici, les deux se posaient à la
  // main dans la base : ni écran, ni route serveur.
  //
  // `formulaireGroupe` vaut `null` quand le modal est fermé, `{}` pour une
  // création, et la ligne du groupe pour une modification. Un seul état pour
  // les deux gestes : ils partagent le même formulaire.
  const [formulaireGroupe, setFormulaireGroupe] = useState(null);
  const [formulaireRole, setFormulaireRole] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [enregistrementFiche, setEnregistrementFiche] = useState(false);
  const [rolesDuGroupe, setRolesDuGroupe] = useState([]);
  // Un role rattache a un module non souscrit (backend2/config/licenceRoles.js)
  // n'est plus propose. Il reste visible, marque, tant que des employes le
  // detiennent : sinon ils sembleraient n'avoir aucun role.
  const rolesVisibles = rolesDuGroupe.filter(
    (r) => r.sous_licence !== false || Number(r.nombre_employes) > 0
  );
  // `tousLesRoles` sert au rattachement : pour proposer un rôle existant, il
  // faut connaître ceux des AUTRES groupes, que `getByGroupeId` ne donne pas.
  const [tousLesRoles, setTousLesRoles] = useState([]);
  const [rattachementOuvert, setRattachementOuvert] = useState(false);
  const [rolesARattacher, setRolesARattacher] = useState([]);
  // `{ role, cible }` pendant un détachement, `null` sinon.
  const [detachement, setDetachement] = useState(null);

  // Les rôles du groupe sélectionné. Un groupe SANS rôle ne peut porter que
  // des portes de module : `updatePermissions` ignore toute autre permission
  // et se contente d'un `console.warn`. La liste est donc affichée, pas cachée.
  const chargerRoles = useCallback(async (groupeId) => {
    if (!groupeId) {
      setRolesDuGroupe([]);
      return;
    }
    try {
      const reponse = await api.roles.getByGroupeId(groupeId);
      setRolesDuGroupe(Array.isArray(reponse?.data) ? reponse.data : []);
    } catch (e) {
      console.error('[Roles] Chargement impossible :', e);
      setRolesDuGroupe([]);
    }
  }, []);

  useEffect(() => {
    chargerRoles(selectedGroup?.id);
  }, [selectedGroup?.id, chargerRoles]);

  const chargerTousLesRoles = useCallback(async () => {
    try {
      const reponse = await api.roles.getAll();
      setTousLesRoles(Array.isArray(reponse?.data) ? reponse.data : []);
    } catch (e) {
      console.error('[Roles] Catalogue indisponible :', e);
      setTousLesRoles([]);
    }
  }, []);

  const ouvrirRattachement = async () => {
    setRolesARattacher([]);
    await chargerTousLesRoles();
    setRattachementOuvert(true);
  };

  // `setRoles` attend la liste COMPLÈTE des rôles que le groupe doit porter :
  // on envoie donc les siens plus les nouveaux. Ne transmettre que les ajouts
  // ferait détacher tous les autres.
  const rattacher = async () => {
    if (!selectedGroup || rolesARattacher.length === 0) return;
    setEnregistrementFiche(true);
    try {
      const complet = [...rolesDuGroupe.map((r) => r.id), ...rolesARattacher];
      const reponse = await api.groupesSecurite.setRoles(selectedGroup.id, complet);

      if (reponse?.success) {
        avertir(reponse.message || 'Rôles rattachés');
        setRattachementOuvert(false);
        setRolesARattacher([]);
        await chargerRoles(selectedGroup.id);
        // Les portes de modules des employés déplacés ont changé : la liste
        // des groupes affiche des compteurs qui ne sont plus à jour.
        await loadData();
      } else {
        avertir(reponse?.message || reponse?.error || 'Rattachement impossible', 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setEnregistrementFiche(false);
    }
  };

  /**
   * Détacher, c'est déplacer.
   *
   * `roles.groupe_securite_id` est NOT NULL : un rôle appartient toujours à un
   * groupe. On passe par `api.roles.update`, dont la transaction déplace aussi
   * `employes_roles.groupe_securite_id` — sinon les porteurs resteraient
   * accrochés aux portes de modules de l'ancien groupe.
   */
  const detacher = async () => {
    if (!detachement?.role || !detachement?.cible) return;
    setEnregistrementFiche(true);
    try {
      const reponse = await api.roles.update(detachement.role.id, {
        nom: detachement.role.nom,
        description: detachement.role.description,
        groupe_securite_id: detachement.cible
      });

      if (reponse?.success) {
        const nomCible = securityGroups.find((g) => g.id === detachement.cible)?.nom;
        avertir(`« ${detachement.role.nom} » déplacé vers « ${nomCible} »`);
        setDetachement(null);
        await chargerRoles(selectedGroup?.id);
        await loadData();
      } else {
        avertir(reponse?.message || reponse?.error || 'Déplacement impossible', 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setEnregistrementFiche(false);
    }
  };

  const enregistrerGroupe = async () => {
    if (!formulaireGroupe) return;
    setEnregistrementFiche(true);
    try {
      const charge = {
        nom: formulaireGroupe.nom?.trim(),
        description: formulaireGroupe.description?.trim() || null
      };
      const reponse = formulaireGroupe.id
        ? await api.groupesSecurite.update(formulaireGroupe.id, charge)
        : await api.groupesSecurite.create(charge);

      if (reponse?.success) {
        avertir(reponse.message || 'Groupe enregistré');
        setFormulaireGroupe(null);
        await loadData();
      } else {
        avertir(reponse?.message || reponse?.error || 'Enregistrement impossible', 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setEnregistrementFiche(false);
    }
  };

  const enregistrerRole = async () => {
    if (!formulaireRole) return;
    setEnregistrementFiche(true);
    try {
      const charge = {
        nom: formulaireRole.nom?.trim(),
        description: formulaireRole.description?.trim() || null,
        groupe_securite_id: formulaireRole.groupe_securite_id
      };
      const reponse = formulaireRole.id
        ? await api.roles.update(formulaireRole.id, charge)
        : await api.roles.create(charge);

      if (reponse?.success) {
        avertir(reponse.message || 'Rôle enregistré');
        setFormulaireRole(null);
        await chargerRoles(selectedGroup?.id);
        // Les permissions d'un groupe sont l'union de ses portes de modules et
        // de celles de ses RÔLES : toucher à un rôle change le compte affiché
        // sur la carte du groupe.
        await loadData();
      } else {
        avertir(reponse?.message || reponse?.error || 'Enregistrement impossible', 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setEnregistrementFiche(false);
    }
  };

  // Le serveur refuse en 409 et DIT pourquoi — « porte encore 50 employés et
  // 2 rôles ». On remonte ce message tel quel : il est plus utile que
  // « suppression impossible ».
  const supprimer = async () => {
    if (!aSupprimer) return;
    setEnregistrementFiche(true);
    try {
      const reponse = aSupprimer.type === 'groupe'
        ? await api.groupesSecurite.remove(aSupprimer.id)
        : await api.roles.remove(aSupprimer.id);

      if (reponse?.success) {
        avertir(reponse.message || 'Supprimé');
        setASupprimer(null);
        if (aSupprimer.type === 'groupe') {
          setSelectedGroup(null);
          await loadData();
        } else {
          await chargerRoles(selectedGroup?.id);
          // Sans ce rechargement, la carte du groupe gardait le compte d'avant.
          // Constaté sur « Cantine scolaire » : deux rôles retirés, et la carte
          // affichait toujours 68 permissions alors que la base n'en portait
          // plus aucune.
          await loadData();
        }
      } else {
        avertir(reponse?.message || reponse?.error || 'Suppression impossible', 'error');
      }
    } catch (e) {
      avertir(e.message, 'error');
    } finally {
      setEnregistrementFiche(false);
    }
  };

  // Ce que le bouton d'enregistrement envoie, c'est TOUT le groupe : la
  // comparaison se fait donc au niveau du groupe, pas du seul module ouvert.
  const hasChanges = useMemo(() => {
    if (!selectedGroup) return false;
    const avant = reference[selectedGroup.id];
    if (!avant) return false;
    const apres = selectedGroup.permissions || {};
    const cles = new Set([...Object.keys(avant), ...Object.keys(apres)]);
    return [...cles].some((cle) => Boolean(apres[cle]?.enabled) !== Boolean(avant[cle]));
  }, [selectedGroup, reference]);

  // Remettre le groupe dans l'etat ou le serveur l'a rendu.
  const handleReset = () => {
    if (!selectedGroup) return;
    const avant = reference[selectedGroup.id];
    if (!avant) return;
    const permissions = { ...selectedGroup.permissions };
    Object.keys(avant).forEach((cle) => {
      permissions[cle] = { ...permissions[cle], enabled: avant[cle] };
    });
    const groupe = { ...selectedGroup, permissions };
    setSelectedGroup(groupe);
    setSecurityGroups((prev) => prev.map((g) => (g.id === groupe.id ? groupe : g)));
  };

  // Toggle d'une permission (comme dans ton code original)
  const handleTogglePermission = (permissionKey) => {
    if (!selectedGroup) return;
    
    const currentPermissions = { ...selectedGroup.permissions };
    const currentStatus = currentPermissions[permissionKey]?.enabled || false;
    
    currentPermissions[permissionKey] = {
      ...currentPermissions[permissionKey],
      enabled: !currentStatus
    };
    
    const updatedGroup = {
      ...selectedGroup,
      permissions: currentPermissions
    };
    
    setSelectedGroup(updatedGroup);
    
    // Mettre à jour dans la liste correspondante
    setSecurityGroups(prevGroups => 
      prevGroups.map(group => 
        group.id === selectedGroup.id ? updatedGroup : group
      )
    );
  };

  // Actions en masse pour un sous-module (comme dans ton code original)
  const handleToggleAllSubModulePermissions = (subModuleName, enable) => {
    if (!selectedGroup) return;
    
    const currentPermissions = { ...selectedGroup.permissions };
    
    // Trouver toutes les permissions de ce sous-module
   Object.keys(currentPermissions).forEach(key => {
  if (key.startsWith(`${selectedModule.key}.${subModuleName}.`)) {
        currentPermissions[key] = {
          ...currentPermissions[key],
          enabled: enable
        };
      }
    });
    
    const updatedGroup = {
      ...selectedGroup,
      permissions: currentPermissions
    };
    
    setSelectedGroup(updatedGroup);
    
    setSecurityGroups(prevGroups => 
      prevGroups.map(group => 
        group.id === selectedGroup.id ? updatedGroup : group
      )
    );
  };

  // Basculer d'un coup TOUTES les permissions d'un module pour un groupe
  // entier engage trop de monde pour partir sur un simple clic : CLAUDE.md
  // impose `ConfirmDialog` sur un geste de cette portee.
  // Actions en masse pour tout le module (comme dans ton code original)
  const handleToggleAllModulePermissions = (enable) => {
    if (!selectedGroup) return;
    
    const currentPermissions = { ...selectedGroup.permissions };
    
    // Activer/désactiver toutes les permissions ELEVES
    Object.keys(currentPermissions).forEach(key => {
  if (key.startsWith(`${selectedModule.key}.`)) {
        currentPermissions[key] = {
          ...currentPermissions[key],
          enabled: enable
        };
      }
    });
    
    const updatedGroup = {
      ...selectedGroup,
      permissions: currentPermissions
    };
    
    setSelectedGroup(updatedGroup);
    
    setSecurityGroups(prevGroups => 
      prevGroups.map(group => 
        group.id === selectedGroup.id ? updatedGroup : group
      )
    );
  };

  // Sauvegarder les permissions (comme dans ton code original)
  const savePermissions = async () => {
    if (!selectedGroup) return;
    
    try {
      setIsSaving(true);
      
      const permissionsToSave = {};
      Object.keys(selectedGroup.permissions).forEach(key => {
        const permData = selectedGroup.permissions[key];
        permissionsToSave[key] = {
          enabled: permData.enabled
        };
      });
      
      const response = await api.groupesSecurite.updatePermissions(
        selectedGroup.id,
        permissionsToSave
      );
      
      if (response && response.success) {
        avertir(`Permissions mises à jour pour ${selectedGroup.nom}`);
        // Le nouvel etat du serveur devient la reference : le bouton redevient
        // gris, et le rappel de modifications non enregistrees disparait.
        setReference((prev) => ({
          ...prev,
          [selectedGroup.id]: Object.fromEntries(
            Object.entries(permissionsToSave).map(([cle, v]) => [cle, Boolean(v.enabled)])
          )
        }));

        await refreshPermissions();
        
        if (addSecurityLog) {
          addSecurityLog({
            action: 'UPDATE_PERMISSIONS',
            details: `Permissions mises à jour pour ${selectedGroup.nom}`,
            groupId: selectedGroup.id
          });
        }
      } else {
        avertir(response?.message || "Enregistrement impossible", 'error');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      avertir(error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Obtenir l'icône pour une action (comme dans ton code original)
  const getPermissionIcon = (action) => {
    const iconMap = {
      'ACCESS': Shield,
      'VIEW': Eye,
      'SEARCH': Filter,
      'FILTER': Filter,
      'CREATE': Plus,
      'EDIT': PenSquare,
      'DELETE': Trash2,
      'UPLOAD': Upload,
      'DOWNLOAD': Download,
      'EXPORT_EXCEL': Download,
      'EXPORT_PDF': Download,
      'GENERATE': Plus,
      'APPROVE': Check,
      'REJECT': Trash2,
      'RESTORE': RefreshCw,
      'MANAGE': Settings,
      'PAYMENT_ADD': CreditCard,
      'RECEIPT_GENERATE': FileText,
      'VIEW_HISTORY': BarChart3,
      'VIEW_LIST': Users,
      'PHOTO_MANAGE': Eye,
      'CLASS_CHANGE': RefreshCw,
      'UPLOAD_DOCUMENTS': Upload,
      'MANAGE_FINANCE': CreditCard,
      'GENERATE_MATRICULE': Plus
    };
    return iconMap[action] || CheckSquare;
  };

  // Calculer les statistiques (comme dans ton code original)
  const getGroupStats = () => {
    if (!selectedGroup || !selectedModule) return { total: 0, enabled: 0, disabled: 0 };
    
    const modulePermissions = getModulePermissions();
    
    return {
      total: modulePermissions.length,
      enabled: modulePermissions.filter(p => p.enabled).length,
      disabled: modulePermissions.filter(p => !p.enabled).length
    };
  };
  const modulePermissions = getModulePermissions();
  const stats = getGroupStats();

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#246990]"></div>
          <span className="ml-2">Chargement des permissions...</span>
        </div>
      </div>
    );
  }

  // Ce garde ne couvre QUE le chargement. Il couvrait aussi l'enregistrement :
  // une sauvegarde refusee remplacait tout l'ecran par cet encadre, et le
  // bouton « Reessayer » rechargeait les donnees - donc ecrasait en silence
  // les bascules que l'utilisateur venait de poser.
  if (erreurChargement) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200">
          <CardContent className="p-6">
            <div className="flex items-center text-red-600">
              <AlertCircle className="mr-2" size={20} />
              <span>Erreur : {erreurChargement}</span>
            </div>
            <Button className="mt-4" onClick={loadData} variant="outline">
              Réessayer
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Layout principal avec animation fluide */}
      <div className="grid grid-cols-12 gap-6 min-h-[80vh]">
        
        {/* PANNEAU GAUCHE - Groupes de securite (fixe) */}
        <div className="col-span-3 space-y-6">
          
          {/* Groupes de Sécurité */}
          <Card className="h-fit">
            <CardHeader className="pb-3">
              <div>
                <CardTitle className="flex items-center text-sm">
                  <Shield className="mr-2 shrink-0 text-green-600" size={16} />
                  Groupes de Sécurité
                </CardTitle>
                <CardDescription className="text-xs">
                  Rôles métier - Personnel de l'école
                </CardDescription>
              </div>

              {/* Le bouton sur sa propre rangée, comme celui des rôles : à
                  droite du titre, dans un panneau étroit, il le faisait passer
                  à la ligne. Un groupe se posait jusqu'ici à la main en base. */}
              <button
                type="button"
                onClick={() => setFormulaireGroupe({ nom: '', description: '' })}
                className={`${BOUTON_SECTION} mt-3 w-full justify-center border border-transparent bg-[#246990] text-white shadow-sm hover:bg-[#1f5a7a] hover:shadow-md`}
              >
                <Plus size={13} />
                Nouveau groupe
              </button>
            </CardHeader>
            <CardContent className="space-y-2">
              {securityGroups.map((group) => (
                <div
                  key={group.id}
                  className={`group/carte p-3 rounded-md border cursor-pointer transition-all ${
                    selectedGroup?.id === group.id
                      ? 'bg-green-50 border-green-500 shadow-sm'
                      : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                  }`}
                  onClick={() => handleSelectGroup(group)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 truncate font-medium text-sm">{group.nom}</div>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* `stopPropagation` : la carte entiere selectionne le
                          groupe, ces deux boutons ne doivent pas le faire. */}
                      <button
                        type="button"
                        title="Modifier"
                        onClick={(e) => { e.stopPropagation(); setFormulaireGroupe({ ...group }); }}
                        className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-white hover:text-[#246990] group-hover/carte:opacity-100"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        title="Supprimer"
                        onClick={(e) => { e.stopPropagation(); setASupprimer({ type: 'groupe', id: group.id, nom: group.nom }); }}
                        className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-white hover:text-red-600 group-hover/carte:opacity-100"
                      >
                        <Trash2 size={13} />
                      </button>
                      <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                   {Object.keys(group.permissions).filter(k => group.permissions[k].enabled).length} permissions actives
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* ------------------------------------------------------------
              Les rôles du groupe

              Un groupe SANS rôle ne peut porter que des portes de module :
              `updatePermissions` ignore toute permission de sous-module quand
              le groupe n'a aucun rôle, avec un `console.warn` que personne ne
              lit. La liste est donc mise sous les yeux, pas cachée.
             ------------------------------------------------------------ */}
          {selectedGroup && (
            <Card className="h-fit">
              <CardHeader className="pb-3">
                <div>
                  <div className="min-w-0">
                    <CardTitle className="flex items-center text-sm">
                      <Layers className="mr-2 shrink-0 text-[#246990]" size={16} />
                      Rôles du groupe
                    </CardTitle>
                    <CardDescription className="text-xs truncate">
                      {selectedGroup.nom}
                    </CardDescription>
                  </div>
                </div>

                {/* Les deux boutons sur leur propre rangée. À côté du titre,
                    dans un panneau `col-span-3`, ils ne laissaient plus la
                    place : « Rôles du groupe » passait à la ligne et le nom du
                    groupe se terminait en « Cantine sc... ».
                    « Rattacher » va chercher un rôle qui existe déjà ailleurs,
                    « Nouveau » en crée un. */}
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={ouvrirRattachement}
                    className={`${BOUTON_SECTION} flex-1 justify-center bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300`}
                  >
                    <Link2 size={13} />
                    Rattacher
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormulaireRole({
                      nom: '', description: '', groupe_securite_id: selectedGroup.id
                    })}
                    className={`${BOUTON_SECTION} flex-1 justify-center border border-transparent bg-[#246990] text-white shadow-sm hover:bg-[#1f5a7a] hover:shadow-md`}
                  >
                    <Plus size={13} />
                    Nouveau
                  </button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {rolesVisibles.length === 0 ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    Ce groupe n'a aucun rôle. Il ne peut donc recevoir que les
                    portes de modules : toute permission plus fine sera ignorée
                    à l'enregistrement.
                  </div>
                ) : (
                  rolesVisibles.map((role) => (
                    <div
                      key={role.id}
                      className="group/role flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-gray-900">{role.nom}</span>
                          {role.sous_licence === false && (
                            <span
                              className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
                              title="Ce rôle relève d'un module non inclus dans la licence : il n'est plus attribuable."
                            >
                              Hors licence · {Number(role.nombre_employes)} employé{Number(role.nombre_employes) > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {role.description && (
                          <div className="truncate text-[11px] text-gray-400">{role.description}</div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          title="Modifier"
                          onClick={() => setFormulaireRole({ ...role })}
                          className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-50 hover:text-[#246990] group-hover/role:opacity-100"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          title="Détacher de ce groupe"
                          onClick={() => setDetachement({
                            role,
                            cible: securityGroups.find((g) => g.id !== selectedGroup.id)?.id || null
                          })}
                          className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-50 hover:text-amber-600 group-hover/role:opacity-100"
                        >
                          <Link2Off size={13} />
                        </button>
                        <button
                          type="button"
                          title="Supprimer"
                          onClick={() => setASupprimer({ type: 'role', id: role.id, nom: role.nom })}
                          className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-50 hover:text-red-600 group-hover/role:opacity-100"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* PANNEAU CENTRAL - Modules (avec animation) */}
        <div className={`transition-all duration-500 ease-in-out ${
          isModuleExpanded ? 'col-span-1' : 'col-span-5'
        }`}>
          <Card className="h-full overflow-hidden">
            <CardHeader className={`transition-all duration-300 ${
              isModuleExpanded ? 'pb-2' : 'pb-4'
            }`}>
              <CardTitle className={`flex items-center transition-all duration-300 ${
                isModuleExpanded ? 'text-sm' : 'text-base'
              }`}>
                {isModuleExpanded ? (
                  <div className="flex flex-col items-center space-y-2 w-full">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleBackToModules}
                      className="w-full flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Retour
                    </Button>
                    {selectedModule && (
                      <div className="text-center">
                        <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${selectedModule.color} text-white mb-1`}>
                          <selectedModule.icon size={16} />
                        </div>
                        <div className="text-xs font-medium text-gray-700">
                          {selectedModule.name}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Boxes className="mr-2 text-[#246990]" size={18} />
                    Modules de l'Application
                  </>
                )}
              </CardTitle>
              {selectedGroup && !isModuleExpanded && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{selectedGroup.nom}</span>
                  <ChevronRight size={14} className="text-gray-400" />
                  <span className="text-blue-600">Sélectionner un module</span>
                </div>
              )}
            </CardHeader>
            
            {!isModuleExpanded && (
              <CardContent className="transition-opacity duration-300">
                {selectedGroup ? (
                  <div className="grid grid-cols-1 gap-4">
                    {modulesAffiches.map((module) => {
                      const ModuleIcon = module.icon;
                      const isSelected = selectedModule?.key === module.key;
                      const ferme = !module.sousLicence;

                      return (
                        <div
                          key={module.key}
                          className={`p-4 border rounded-lg transition-all ${
                            ferme
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                              : isSelected
                                ? 'border-blue-500 bg-blue-50 shadow-md cursor-pointer'
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-sm cursor-pointer'
                          }`}
                          onClick={ferme ? undefined : () => handleSelectModule(module)}
                          title={ferme ? "Ce module n'est pas inclus dans la licence de l'établissement" : undefined}
                        >
                          <div className="flex items-center">
                            <div
                              className={`inline-flex items-center justify-center w-12 h-12 rounded-lg text-white mr-4 ${
                                ferme ? 'bg-gray-300' : module.color
                              }`}
                            >
                              {ferme ? <Lock size={22} /> : <ModuleIcon size={24} />}
                            </div>
                            <div className="flex-1">
                              <div className={`font-medium text-sm mb-1 ${ferme ? 'text-gray-500' : ''}`}>
                                {module.name}
                              </div>
                              <div className="text-xs text-gray-500 leading-tight">
                                {ferme
                                  ? "Non inclus dans la licence de l'établissement"
                                  : module.description}
                              </div>
                              {isSelected && !ferme && (
                                <Badge variant="default" className="bg-blue-500 text-xs mt-2">
                                  Sélectionné
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {applicationModules.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen size={48} className="mx-auto mb-4 text-gray-300" />
                        <h3 className="font-medium mb-2">Aucun module disponible</h3>
                        <p className="text-sm">Les modules seront affichés une fois configurés dans le backend</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen size={48} className="mx-auto mb-4 text-gray-300" />
                    <h3 className="font-medium mb-2">Aucun groupe sélectionné</h3>
                    <p className="text-sm">Sélectionnez un groupe à gauche pour voir les modules</p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>

        {/* PANNEAU DROIT - Permissions (avec expansion) */}
        <div className={`transition-all duration-500 ease-in-out ${
          isModuleExpanded ? 'col-span-8' : 'col-span-4'
        }`}>
          <Card className="h-full">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="flex items-center text-base">
                    <KeyRound className="mr-2 text-[#246990]" size={18} />
                    Permissions du module
                    {selectedModule && (
                      <Badge variant="outline" className="ml-2">
                        {selectedModule.name}
                      </Badge>
                    )}
                  </CardTitle>
                  {selectedGroup && selectedModule && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <span>{selectedGroup.nom}</span>
                      <ChevronRight size={14} className="text-gray-400" />
                      <span>{selectedModule.name}</span>
                      <ChevronRight size={14} className="text-gray-400" />
                      <span className="text-blue-600">Permissions</span>
                    </div>
                  )}
                </div>
                {selectedGroup && selectedModule && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={isSaving || !hasChanges}
                      className={`${BOUTON_SECTION} bg-white border border-gray-200 text-gray-500 ${
                        hasChanges
                          ? 'hover:bg-gray-50 hover:border-gray-300'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <RotateCcw size={13} />
                      Réinitialiser
                    </button>
                    {/* Gris tant que rien n'a change : un bouton actif en
                        permanence n'apprend rien (PARAMETRAGE-MODULES.md §5). */}
                    <button
                      type="button"
                      onClick={savePermissions}
                      disabled={isSaving || !hasChanges}
                      className={`${BOUTON_SECTION} border border-transparent text-white shadow-sm ${
                        hasChanges
                          ? 'bg-[#246990] hover:bg-[#1f5a7a] hover:shadow-md'
                          : 'bg-gray-300 cursor-not-allowed shadow-none'
                      }`}
                    >
                      {isSaving ? (
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white" />
                      ) : (
                        <Save size={13} />
                      )}
                      Enregistrer
                    </button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selectedGroup && selectedModule ? (
                <div className="space-y-4">
                  
                  {/* Quatre chiffres, toujours les quatre. Le dernier
                      n'apparaissait qu'en mode elargi : une statistique qui
                      va et vient selon la largeur du panneau n'en est pas une.
                      Le violet a disparu avec lui - il n'est pas dans la
                      palette (DESIGN-SYSTEM.md). */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="rounded-lg bg-gray-100 p-2 text-center">
                      <div className="font-bold tabular-nums text-gray-900">{stats.total}</div>
                      <div className="text-xs text-gray-600">Total</div>
                    </div>
                    <div className="rounded-lg bg-green-50 p-2 text-center">
                      <div className="font-bold tabular-nums text-green-700">{stats.enabled}</div>
                      <div className="text-xs text-green-600">Activées</div>
                    </div>
                    <div className="rounded-lg border border-gray-200 bg-white p-2 text-center">
                      <div className="font-bold tabular-nums text-gray-500">{stats.disabled}</div>
                      <div className="text-xs text-gray-500">Désactivées</div>
                    </div>
                    <div className="rounded-lg bg-[#246990]/10 p-2 text-center">
                      <div className="font-bold tabular-nums text-[#246990]">
                        {modulePermissions.filter(p => p.isSystemDefault && p.enabled).length}
                      </div>
                      <div className="text-xs text-[#246990]">Par défaut</div>
                    </div>
                  </div>

                  {/* Liste des permissions avec layout adaptatif */}
                  <div className={`space-y-3 overflow-y-auto transition-all duration-300 ${
                    isModuleExpanded ? 'max-h-[60vh]' : 'max-h-96'
                  }`}>
                    {Object.entries(
                      modulePermissions.reduce((acc, perm) => {
                        if (!acc[perm.subModule]) acc[perm.subModule] = [];
                        acc[perm.subModule].push(perm);
                        return acc;
                      }, {})
                    ).map(([subModule, perms]) => (
                      <div key={subModule} className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-3 py-2 border-b">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-900">
                              {perms[0].subModuleDisplayName}
                            </h3>
                            <Badge variant="outline" className="text-xs">
                              {perms.filter(p => p.enabled).length}/{perms.length}
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-2 p-3">
                          <div className={`grid gap-2 ${
                            isModuleExpanded ? 'grid-cols-2' : 'grid-cols-1'
                          }`}>
                            {perms.map(permission => {
                              const PermIcon = getPermissionIcon(permission.action);

                              // Un droit non accorde etait peint en rouge -
                              // bordure, fond et texte. Le rouge est la couleur
                              // de l'erreur et du destructeur
                              // (DESIGN-SYSTEM.md), or ne pas donner une
                              // permission est l'etat NORMAL de la plupart des
                              // 215 : l'ecran entier avait l'air en panne. Le
                              // neutre dit la meme chose sans accuser.
                              return (
                                <div
                                  key={permission.key}
                                  className={`flex items-center justify-between rounded border p-2.5 transition-colors ${
                                    permission.enabled
                                      ? 'bg-green-50 border-green-200'
                                      : 'bg-white border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center flex-1 min-w-0">
                                    <PermIcon
                                      className={`mr-2 shrink-0 ${permission.enabled ? 'text-green-600' : 'text-gray-400'}`}
                                      size={16}
                                    />
                                    <div className="min-w-0">
                                      <div className={`text-sm font-medium ${
                                        permission.enabled ? 'text-green-800' : 'text-gray-700'
                                      }`}>
                                        {permission.libelle || permission.action}
                                      </div>
                                      {/* La cle technique reste lisible, en
                                          second : c'est elle qu'on cherche
                                          quand on lit un journal d'audit ou
                                          qu'on en parle au support. */}
                                      {permission.libelle && (
                                        <div className="text-[11px] text-gray-400">
                                          {permission.action}
                                        </div>
                                      )}
                                      {permission.isSystemDefault && (
                                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 mt-1">
                                          Par défaut
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <Switch
                                    checked={permission.enabled === true}
                                    onCheckedChange={() => handleTogglePermission(permission.key)}
                                    className="ml-2 shrink-0"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* `Trash2` portait « Tout desactiver » : la corbeille pour
                      un geste qui ne supprime rien. */}
                  <div className="flex gap-2 border-t pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBasculeEnMasse(true)}
                      className="flex-1 text-green-700 hover:text-green-800"
                    >
                      <ShieldCheck size={14} className="mr-1" />
                      Tout activer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBasculeEnMasse(false)}
                      className="flex-1 text-gray-600 hover:text-gray-800"
                    >
                      <ShieldOff size={14} className="mr-1" />
                      Tout désactiver
                    </Button>
                  </div>

                  {/* Le filet qui rattrape le changement de module ou d'onglet :
                      la liste defile, le bouton du haut sort du champ, et rien
                      ne dirait autrement que le travail n'est pas enregistre. */}
                  {hasChanges && (
                    <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                        <AlertCircle size={15} className="text-amber-600" />
                      </div>
                      <p className="text-xs font-semibold text-amber-700">
                        Vous avez des modifications non enregistrées.
                      </p>
                      <button
                        type="button"
                        onClick={savePermissions}
                        disabled={isSaving}
                        className={`${BOUTON_SECTION} ml-auto shrink-0 border border-transparent bg-[#246990] text-white shadow-sm hover:bg-[#1f5a7a] hover:shadow-md`}
                      >
                        {isSaving ? 'Enregistrement…' : 'Enregistrer maintenant'}
                      </button>
                    </div>
                  )}
                </div>
              ) : selectedGroup && syntheseGroupe ? (
                /* Aucun module choisi : plutot qu'un message, tout ce que le
                   groupe porte, replie par module. */
                <div className="space-y-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Tout ce que « {selectedGroup.nom} » peut faire
                      </h3>
                      <p className="text-xs text-gray-500">
                        Ouvrez un module pour modifier ses droits.
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-semibold tabular-nums text-gray-900">
                        {syntheseGroupe.total}
                      </div>
                      <div className="text-xs text-gray-500">actives</div>
                    </div>
                  </div>

                  {syntheseGroupe.total === 0 ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      Ce groupe n'a aucune permission active : les employés qui en
                      dépendent n'accèdent à rien.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[58vh] overflow-y-auto pr-1">
                      {syntheseGroupe.lignes.map((ligne) => {
                        const Icone = ligne.icon;
                        const ouvrable = ligne.sousLicence && ligne.auCatalogue;
                        return (
                          <div key={ligne.key} className="rounded-md border border-gray-200">
                            <button
                              type="button"
                              disabled={!ouvrable}
                              onClick={() => ouvrable && handleSelectModule(ligne.auCatalogue)}
                              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left ${
                                ouvrable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                              }`}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <Icone size={14} className="shrink-0 text-[#246990]" />
                                <span className="truncate text-sm font-medium text-gray-900">
                                  {ligne.name}
                                </span>
                                {/* Un droit accorde sur un module non souscrit ne
                                    produit rien : la garde serveur refusera. On le
                                    montre plutot que de le taire. */}
                                {!ligne.sousLicence && (
                                  <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                    hors licence
                                  </span>
                                )}
                                {!ligne.auCatalogue && (
                                  <span className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                                    hors catalogue
                                  </span>
                                )}
                              </span>
                              <span className="flex shrink-0 items-center gap-1 text-xs text-gray-500">
                                {ligne.droits.length}
                                {ouvrable && <ChevronRight size={13} className="text-gray-400" />}
                              </span>
                            </button>

                            <ul className="border-t border-gray-100 px-3 py-2 space-y-1">
                              {ligne.droits.map((droit) => (
                                <li key={droit.cle} className="flex gap-2 text-xs">
                                  <Check size={11} className="mt-[3px] shrink-0 text-green-600" />
                                  {/* La description est une phrase : on la laisse
                                      passer a la ligne plutot que de la tronquer. */}
                                  <span className="min-w-0">
                                    <span className="text-gray-700">{droit.libelle}</span>
                                    {droit.sousModuleLibelle && (
                                      <span className="text-gray-400"> · {droit.sousModuleLibelle}</span>
                                    )}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(syntheseGroupe.horsLicence > 0 || syntheseGroupe.horsCatalogue > 0) && (
                    <p className="flex items-start gap-2 text-xs text-gray-500">
                      <Info size={13} className="mt-0.5 shrink-0 text-gray-400" />
                      <span>
                        {syntheseGroupe.horsLicence > 0 && (
                          <>
                            {syntheseGroupe.horsLicence} module(s) portent des droits que
                            la licence de l'école n'ouvre pas : ils sont conservés et
                            reprendront effet si elle change.{' '}
                          </>
                        )}
                        {syntheseGroupe.horsCatalogue > 0 && (
                          <>
                            {syntheseGroupe.horsCatalogue} module(s) ne figurent plus au
                            catalogue des permissions : aucune carte ne les porte, ils ne
                            sont visibles qu'ici.
                          </>
                        )}
                      </span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Shield size={48} className="mx-auto mb-4 text-gray-300" />
                  <h3 className="font-medium mb-2">Sélectionnez un groupe</h3>
                  <p className="text-sm">
                    Choisissez un groupe de sécurité à gauche pour voir tout ce qu'il permet.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      <ConfirmDialog
        isOpen={basculeEnMasse !== null}
        onClose={() => setBasculeEnMasse(null)}
        onConfirm={() => {
          handleToggleAllModulePermissions(basculeEnMasse);
          setBasculeEnMasse(null);
        }}
        type={basculeEnMasse ? 'question' : 'warning'}
        title={basculeEnMasse ? 'Activer tout le module ?' : 'Désactiver tout le module ?'}
        description={
          selectedModule && selectedGroup
            ? basculeEnMasse
              ? `Les ${stats.total} permissions de « ${selectedModule.name} » seront cochées pour le groupe « ${selectedGroup.nom} ». Rien ne part au serveur avant que vous enregistriez.`
              : `Les ${stats.total} permissions de « ${selectedModule.name} » seront décochées pour le groupe « ${selectedGroup.nom} » : ses employés perdront l'accès au module. Rien ne part au serveur avant que vous enregistriez.`
            : ''
        }
        confirmText={basculeEnMasse ? 'Tout activer' : 'Tout désactiver'}
      />

      {/* ------------------------------------------------------------------
          Créer ou modifier un groupe de sécurité
         ------------------------------------------------------------------ */}
      <Dialog
        open={Boolean(formulaireGroupe)}
        onOpenChange={(ouvert) => { if (!ouvert) setFormulaireGroupe(null); }}
      >
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Shield size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {formulaireGroupe?.id ? 'Modifier le groupe' : 'Nouveau groupe de sécurité'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Un groupe porte les portes de modules ; ses rôles portent le détail.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700">
                Nom <span className="text-red-600">*</span>
              </Label>
              <input
                type="text"
                value={formulaireGroupe?.nom || ''}
                onChange={(e) => setFormulaireGroupe((p) => ({ ...p, nom: e.target.value }))}
                placeholder="Direction Générale"
                className="mt-1.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
              <p className="mt-1 text-xs text-gray-400">
                Trois caractères au minimum, et unique dans l'établissement.
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700">Description</Label>
              <textarea
                rows={3}
                value={formulaireGroupe?.description || ''}
                onChange={(e) => setFormulaireGroupe((p) => ({ ...p, description: e.target.value }))}
                placeholder="Qui appartient à ce groupe, et pourquoi"
                className="mt-1.5 w-full rounded-lg border border-gray-200 p-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
            </div>
            {!formulaireGroupe?.id && (
              <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 p-3 text-xs text-gray-600">
                Le groupe sera créé sans aucune permission. Pensez ensuite à lui
                rattacher au moins un rôle : sans rôle, il ne peut porter que les
                portes de modules.
              </div>
            )}
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setFormulaireGroupe(null)}
              disabled={enregistrementFiche}
            >
              Annuler
            </Button>
            <Button
              onClick={enregistrerGroupe}
              disabled={enregistrementFiche || (formulaireGroupe?.nom || '').trim().length < 3}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              <Save className="h-4 w-4" />
              {enregistrementFiche ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          Créer ou modifier un rôle
         ------------------------------------------------------------------ */}
      <Dialog
        open={Boolean(formulaireRole)}
        onOpenChange={(ouvert) => { if (!ouvert) setFormulaireRole(null); }}
      >
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Layers size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {formulaireRole?.id ? 'Modifier le rôle' : 'Nouveau rôle'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Un rôle appartient toujours à un groupe, et c'est de lui qu'il tient ses portes.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div>
              <Label className="text-sm font-semibold text-gray-700">
                Nom <span className="text-red-600">*</span>
              </Label>
              <input
                type="text"
                value={formulaireRole?.nom || ''}
                onChange={(e) => setFormulaireRole((p) => ({ ...p, nom: e.target.value }))}
                placeholder="Secrétaire de Direction"
                className="mt-1.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
              <p className="mt-1 text-xs text-gray-400">
                Unique dans tout l'établissement : à l'embauche, les rôles sont
                proposés dans une liste plate, sans leur groupe.
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700">Groupe de sécurité</Label>
              {/* La primitive `Select` et non un `<select>` natif : elle est
                  importée dans ce fichier depuis toujours, et servait nulle
                  part. `value` doit être une chaîne. */}
              <Select
                value={formulaireRole?.groupe_securite_id ? String(formulaireRole.groupe_securite_id) : ''}
                onValueChange={(valeur) => setFormulaireRole((p) => ({
                  ...p, groupe_securite_id: parseInt(valeur, 10)
                }))}
              >
                <SelectTrigger className="mt-1.5 h-9 border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
                  <SelectValue placeholder="Choisir un groupe" />
                </SelectTrigger>
                <SelectContent>
                  {securityGroups.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>{g.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formulaireRole?.id && (
                <p className="mt-1 text-xs text-amber-700">
                  Changer de groupe déplace aussi les employés qui portent ce rôle :
                  ce sont les portes du nouveau groupe qu'ils auront.
                </p>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-700">Description</Label>
              <textarea
                rows={3}
                value={formulaireRole?.description || ''}
                onChange={(e) => setFormulaireRole((p) => ({ ...p, description: e.target.value }))}
                className="mt-1.5 w-full rounded-lg border border-gray-200 p-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              />
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setFormulaireRole(null)}
              disabled={enregistrementFiche}
            >
              Annuler
            </Button>
            <Button
              onClick={enregistrerRole}
              disabled={enregistrementFiche || (formulaireRole?.nom || '').trim().length < 3}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              <Save className="h-4 w-4" />
              {enregistrementFiche ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          Rattacher des rôles existants

          Un rôle appartient à un seul groupe : le rattacher ici, c'est le
          RETIRER de son groupe actuel. Ses porteurs suivent — ce sont les
          portes de modules du nouveau groupe qu'ils auront. La liste le dit
          pour chaque ligne plutôt qu'une fois en bas de page.
         ------------------------------------------------------------------ */}
      <Dialog
        open={rattachementOuvert}
        onOpenChange={(ouvert) => { if (!ouvert) setRattachementOuvert(false); }}
      >
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Link2 size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Rattacher des rôles existants
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Vers « {selectedGroup?.nom} ». Un rôle quitte son groupe actuel pour celui-ci.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {(() => {
              const disponibles = tousLesRoles.filter(
                (r) => Number(r.groupe_securite_id) !== Number(selectedGroup?.id)
              );

              if (disponibles.length === 0) {
                return (
                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
                    <Layers size={30} className="mx-auto text-gray-300" />
                    <p className="mt-3 text-sm text-gray-500">
                      Tous les rôles de l'établissement appartiennent déjà à ce groupe.
                    </p>
                  </div>
                );
              }

              return (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="w-12 px-5 py-3"></th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Rôle
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Groupe actuel
                        </th>
                        <th className="w-40 px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                          Conséquence
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {disponibles.map((role, index) => {
                        const choisi = rolesARattacher.includes(role.id);
                        return (
                          <tr
                            key={role.id}
                            onClick={() => setRolesARattacher((p) => (
                              p.includes(role.id) ? p.filter((n) => n !== role.id) : [...p, role.id]
                            ))}
                            className={`cursor-pointer border-b border-gray-100 last:border-b-0 ${
                              choisi ? 'bg-[#246990]/5' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                            }`}
                          >
                            <td className="px-5 py-3.5">
                              <input
                                type="checkbox"
                                checked={choisi}
                                readOnly
                                className="h-4 w-4 rounded border-gray-300 accent-[#246990]"
                              />
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="text-sm font-medium text-gray-900">{role.nom}</div>
                              {role.description && (
                                <div className="text-[11px] text-gray-400">{role.description}</div>
                              )}
                            </td>
                            <td className="px-5 py-3.5 text-sm text-gray-600">
                              {role.groupe_securite_nom || '—'}
                            </td>
                            <td className="px-5 py-3.5 text-right">
                              {Number(role.nombre_employes) > 0 ? (
                                <Badge variant="outline" className="border-amber-300 text-xs text-amber-700">
                                  {role.nombre_employes} employé{Number(role.nombre_employes) > 1 ? 's' : ''} suivront
                                </Badge>
                              ) : (
                                <span className="text-xs text-gray-300">aucun porteur</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setRattachementOuvert(false)}
              disabled={enregistrementFiche}
            >
              Annuler
            </Button>
            <Button
              onClick={rattacher}
              disabled={enregistrementFiche || rolesARattacher.length === 0}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              <Link2 className="h-4 w-4" />
              {enregistrementFiche
                ? 'Rattachement…'
                : `Rattacher${rolesARattacher.length > 0 ? ` (${rolesARattacher.length})` : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          Détacher un rôle — c'est-à-dire le déplacer

          Le schéma interdit un rôle sans groupe (`groupe_securite_id` est NOT
          NULL). Plutôt que d'afficher un bouton « Détacher » qui échouerait,
          on demande où il va.
         ------------------------------------------------------------------ */}
      <Dialog
        open={Boolean(detachement)}
        onOpenChange={(ouvert) => { if (!ouvert) setDetachement(null); }}
      >
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Link2Off size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Détacher « {detachement?.role?.nom} »
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                De « {selectedGroup?.nom} » vers un autre groupe.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 p-3 text-xs text-gray-600">
              Un rôle appartient toujours à un groupe : c'est de lui qu'il tient
              ses portes de modules. Il ne peut donc pas être détaché dans le
              vide — il change de groupe.
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-700">
                Nouveau groupe <span className="text-red-600">*</span>
              </Label>
              <Select
                value={detachement?.cible ? String(detachement.cible) : ''}
                onValueChange={(valeur) => setDetachement((p) => ({
                  ...p, cible: parseInt(valeur, 10)
                }))}
              >
                <SelectTrigger className="mt-1.5 h-9 border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
                  <SelectValue placeholder="Choisir le groupe d'accueil" />
                </SelectTrigger>
                <SelectContent>
                  {securityGroups
                    .filter((g) => g.id !== selectedGroup?.id)
                    .map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>{g.nom}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {securityGroups.filter((g) => g.id !== selectedGroup?.id).length === 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Il n'existe aucun autre groupe où le placer. Créez-en un d'abord.
              </div>
            )}

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Les employés qui portent ce rôle suivront : ce sont les portes de
              modules du nouveau groupe qu'ils auront.
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setDetachement(null)}
              disabled={enregistrementFiche}
            >
              Annuler
            </Button>
            <Button
              onClick={detacher}
              disabled={enregistrementFiche || !detachement?.cible}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              <Link2Off className="h-4 w-4" />
              {enregistrementFiche ? 'Déplacement…' : 'Détacher'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Le serveur refuse en 409 quand quelqu'un s'appuie encore dessus, et
          le dit avec les nombres. Ce dialogue prévient, il ne promet pas. */}
      <ConfirmDialog
        isOpen={Boolean(aSupprimer)}
        onClose={() => setASupprimer(null)}
        onConfirm={supprimer}
        isLoading={enregistrementFiche}
        type="warning"
        title={aSupprimer?.type === 'groupe' ? 'Supprimer ce groupe ?' : 'Supprimer ce rôle ?'}
        description={
          aSupprimer
            ? `« ${aSupprimer.nom} » et ses permissions seront supprimés. `
              + (aSupprimer.type === 'groupe'
                ? "La suppression sera refusée si des employés ou des rôles y sont encore rattachés."
                : "La suppression sera refusée si des employés le portent encore.")
            : ''
        }
        confirmText="Supprimer"
      />

      {/* Le retour passait par un bandeau maison `fixed bottom-4 right-4`, pose
          par `setTimeout(…, 3000)`. CLAUDE.md : `Toast`. */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default RolesPermissionsManager;